import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const WorkoutSession = ({ program, onComplete, onClose }) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [sets, setSets] = useState([]);
  const [isResting, setIsResting] = useState(false);
  const [restTimer, setRestTimer] = useState(0);
  const [notes, setNotes] = useState('');

  const currentExercise = program?.exercises?.[currentExerciseIndex];

  useEffect(() => {
    if (currentExercise) {
      setSets(
        Array(currentExercise?.sets)?.fill(null)?.map(() => ({
          reps: currentExercise?.reps,
          weight: '',
          completed: false,
        }))
      );
    }
  }, [currentExerciseIndex, currentExercise]);

  useEffect(() => {
    let interval;
    if (isResting && restTimer > 0) {
      interval = setInterval(() => {
        setRestTimer((prev) => {
          if (prev <= 1) {
            setIsResting(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isResting, restTimer]);

  const handleSetComplete = (setIndex) => {
    const newSets = [...sets];
    newSets[setIndex].completed = !newSets?.[setIndex]?.completed;
    setSets(newSets);

    if (!newSets?.[setIndex]?.completed && setIndex < sets?.length - 1) {
      setIsResting(true);
      setRestTimer(currentExercise?.restTime || 60);
    }
  };

  const handleWeightChange = (setIndex, value) => {
    const newSets = [...sets];
    newSets[setIndex].weight = value;
    setSets(newSets);
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < program?.exercises?.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      setIsResting(false);
      setRestTimer(0);
    } else {
      onComplete({
        programId: program?.id,
        exercises: program?.exercises?.map((ex, idx) => ({
          exerciseId: ex?.id,
          sets: idx === currentExerciseIndex ? sets : [],
        })),
        notes,
        completedAt: new Date()?.toISOString(),
      });
    }
  };

  const handlePreviousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
      setIsResting(false);
      setRestTimer(0);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const completedSets = sets?.filter(s => s?.completed)?.length;
  const progressPercentage = (completedSets / sets?.length) * 100;

  return (
    <div className="fixed inset-0 bg-background z-[200] overflow-y-auto">
      <div className="min-h-screen">
        <div className="sticky top-0 bg-card border-b border-border z-10">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  iconName="X"
                />
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                    {program?.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Exercise {currentExerciseIndex + 1} of {program?.exercises?.length}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="Save"
                iconPosition="left"
              >
                Save Progress
              </Button>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
            {isResting && (
              <div className="card bg-warning/10 border-warning">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon name="Timer" size={24} color="var(--color-warning)" />
                    <div>
                      <h3 className="font-semibold text-foreground">Rest Time</h3>
                      <p className="text-sm text-muted-foreground">Take a break before next set</p>
                    </div>
                  </div>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-warning">
                    {formatTime(restTimer)}
                  </div>
                </div>
              </div>
            )}

            <div className="card">
              <div className="relative h-64 md:h-80 lg:h-96 -mx-9 -mt-9 mb-6 overflow-hidden rounded-t-xl">
                <Image
                  src={currentExercise?.videoThumbnail}
                  alt={currentExercise?.videoThumbnailAlt}
                  className="w-full h-full object-cover"
                />
                <button className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Icon name="Play" size={32} />
                  </div>
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
                    {currentExercise?.name}
                  </h3>
                  <p className="text-muted-foreground">
                    {currentExercise?.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Repeat" size={20} color="var(--color-primary)" />
                    <div>
                      <div className="text-xs text-muted-foreground">Sets</div>
                      <div className="font-semibold text-foreground">{currentExercise?.sets}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Hash" size={20} color="var(--color-primary)" />
                    <div>
                      <div className="text-xs text-muted-foreground">Reps</div>
                      <div className="font-semibold text-foreground">{currentExercise?.reps}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Timer" size={20} color="var(--color-primary)" />
                    <div>
                      <div className="text-xs text-muted-foreground">Rest</div>
                      <div className="font-semibold text-foreground">{currentExercise?.restTime}s</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Dumbbell" size={20} color="var(--color-primary)" />
                    <div>
                      <div className="text-xs text-muted-foreground">Equipment</div>
                      <div className="font-semibold text-foreground">{currentExercise?.equipment}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Exercise Progress</span>
                    <span className="font-medium text-foreground">
                      {completedSets}/{sets?.length} sets
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-success transition-all duration-500"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-4">
                Track Your Sets
              </h4>
              <div className="space-y-3">
                {sets?.map((set, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                      set?.completed 
                        ? 'bg-success/10 border-success' :'bg-muted/50 border-border'
                    }`}
                  >
                    <button
                      onClick={() => handleSetComplete(index)}
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                        set?.completed
                          ? 'bg-success border-success text-success-foreground'
                          : 'border-border hover:border-primary'
                      }`}
                    >
                      {set?.completed && <Icon name="Check" size={16} />}
                    </button>
                    <span className="font-medium text-foreground min-w-[60px]">
                      Set {index + 1}
                    </span>
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <Input
                        type="number"
                        placeholder="Weight (lbs)"
                        value={set?.weight}
                        onChange={(e) => handleWeightChange(index, e?.target?.value)}
                        disabled={set?.completed}
                      />
                      <Input
                        type="number"
                        placeholder="Reps"
                        value={set?.reps}
                        onChange={(e) => {
                          const newSets = [...sets];
                          newSets[index].reps = e?.target?.value;
                          setSets(newSets);
                        }}
                        disabled={set?.completed}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-4">
                Workout Notes
              </h4>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e?.target?.value)}
                placeholder="Add notes about your workout, how you felt, any modifications made..."
                className="w-full min-h-[120px] px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handlePreviousExercise}
                disabled={currentExerciseIndex === 0}
                iconName="ChevronLeft"
                iconPosition="left"
                className="flex-1"
              >
                Previous
              </Button>
              <Button
                variant="default"
                onClick={handleNextExercise}
                iconName={currentExerciseIndex === program?.exercises?.length - 1 ? "Check" : "ChevronRight"}
                iconPosition="right"
                className="flex-1"
              >
                {currentExerciseIndex === program?.exercises?.length - 1 ? 'Complete Workout' : 'Next Exercise'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutSession;